import { useEffect } from 'react';

export default function NeoEarthCultureFes() {
  useEffect(() => {
    // Generate stars
    const starsContainer = document.getElementById('stars');
    if (starsContainer) {
      const starCount = 200;
      starsContainer.innerHTML = '';
      
      for (let i = 0; i < starCount; i++) {
        const star = document.createElement('div');
        star.className = 'star';
        star.style.left = Math.random() * 100 + '%';
        star.style.top = Math.random() * 100 + '%';
        star.style.animationDelay = Math.random() * 3 + 's';
        star.style.opacity = String(Math.random() * 0.7 + 0.3);
        const size = Math.random() * 2 + 1;
        star.style.width = size + 'px';
        star.style.height = size + 'px';
        starsContainer.appendChild(star);
      }
    }

    // Fade in on scroll
    const observerOptions = {
      threshold: 0.1,
      rootMargin: '0px 0px -50px 0px'
    };

    const observer = new IntersectionObserver((entries) => {
      entries.forEach(entry => {
        if (entry.isIntersecting) {
          entry.target.classList.add('visible');
        }
      });
    }, observerOptions);

    document.querySelectorAll('.fade-in').forEach(el => {
      observer.observe(el);
    });

    return () => {
      observer.disconnect();
    };
  }, []);

  return (
    <>
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Zen+Kaku+Gothic+New:wght@300;400;500;700&family=Cormorant+Garamond:ital,wght@0,300;0,400;0,600;1,300&display=swap');

        .neofes-root {
          --color-bg-deep: #0a0a12;
          --color-bg-mid: #12121f;
          --color-accent-1: #9d8df7;
          --color-accent-2: #6ee7dc;
          --color-accent-3: #f6ad55;
          --color-text: #ffffff;
          --color-text-muted: #b8b8d0;
          --font-jp: 'Zen Kaku Gothic New', sans-serif;
          --font-en: 'Cormorant Garamond', serif;
        }

        .neofes-root {
          font-family: var(--font-jp);
          background: var(--color-bg-deep);
          color: var(--color-text);
          line-height: 2;
          font-weight: 400;
          font-size: 16px;
          overflow-x: hidden;
          min-height: 100vh;
        }

        /* Cosmic Background */
        .neofes-cosmic-bg {
          position: fixed;
          top: 0;
          left: 0;
          width: 100%;
          height: 100%;
          z-index: -1;
          background: 
            radial-gradient(ellipse at 20% 20%, rgba(123, 104, 238, 0.15) 0%, transparent 50%),
            radial-gradient(ellipse at 80% 80%, rgba(79, 209, 197, 0.1) 0%, transparent 50%),
            radial-gradient(ellipse at 50% 50%, rgba(246, 173, 85, 0.05) 0%, transparent 70%),
            var(--color-bg-deep);
        }

        .neofes-stars {
          position: fixed;
          top: 0;
          left: 0;
          width: 100%;
          height: 100%;
          z-index: 0;
          pointer-events: none;
        }

        .neofes-stars .star {
          position: absolute;
          background: white;
          border-radius: 50%;
          animation: neofes-twinkle 3s infinite ease-in-out;
          box-shadow: 0 0 3px rgba(255, 255, 255, 0.8);
        }

        @keyframes neofes-twinkle {
          0%, 100% { opacity: 0.3; transform: scale(1); }
          50% { opacity: 1; transform: scale(1.2); }
        }

        /* Header */
        .neofes-header {
          min-height: 100vh;
          display: flex;
          flex-direction: column;
          justify-content: center;
          align-items: center;
          text-align: center;
          padding: 2rem 1rem;
          position: relative;
        }

        @media (max-width: 768px) {
          .neofes-header {
            padding: 1rem 1rem;
          }
        }

        .neofes-header::before {
          content: '';
          position: absolute;
          top: 50%;
          left: 50%;
          transform: translate(-50%, -50%);
          width: 600px;
          height: 600px;
          background: radial-gradient(circle, rgba(123, 104, 238, 0.2) 0%, transparent 70%);
          border-radius: 50%;
          animation: neofes-pulse 8s infinite ease-in-out;
        }

        @keyframes neofes-pulse {
          0%, 100% { transform: translate(-50%, -50%) scale(1); opacity: 0.5; }
          50% { transform: translate(-50%, -50%) scale(1.2); opacity: 0.8; }
        }

        .neofes-header-content {
          position: relative;
          z-index: 1;
        }

        .neofes-logo {
          font-size: 3rem;
          font-weight: 700;
          letter-spacing: 0.05em;
          margin-bottom: 1rem;
          background: linear-gradient(135deg, #4facfe 0%, #00f2fe 25%, #a8edea 50%, #fed6e3 75%, #d299c2 100%);
          -webkit-background-clip: text;
          -webkit-text-fill-color: transparent;
          background-clip: text;
          line-height: 1.2;
        }

        @media (max-width: 768px) {
          .neofes-logo {
            font-size: 2.2rem;
            line-height: 1.3;
            max-width: 280px;
            margin-left: auto;
            margin-right: auto;
            margin-bottom: 0.5rem;
          }
        }

        .neofes-year {
          font-family: var(--font-en);
          font-size: 1.8rem;
          letter-spacing: 0.5em;
          color: var(--color-text);
          margin-bottom: 2rem;
          animation: neofes-fadeInUp 1.5s ease-out 0.2s backwards;
        }

        @media (max-width: 768px) {
          .neofes-year {
            font-size: 1.4rem;
            margin-bottom: 1rem;
          }
        }

        .neofes-tagline {
          font-size: 1.6rem;
          line-height: 2.2;
          color: var(--color-text);
          margin-bottom: 2rem;
          animation: neofes-fadeInUp 1.5s ease-out 0.4s backwards;
        }

        @media (max-width: 768px) {
          .neofes-tagline {
            font-size: 1.3rem;
            line-height: 1.8;
            margin-bottom: 1rem;
          }
        }

        .neofes-tagline span {
          display: block;
        }

        .neofes-dates {
          font-family: var(--font-en);
          font-size: 2rem;
          letter-spacing: 0.2em;
          color: var(--color-accent-2);
          margin-bottom: 0.5rem;
          animation: neofes-fadeInUp 1.5s ease-out 0.6s backwards;
        }

        @media (max-width: 768px) {
          .neofes-dates {
            font-size: 1.2rem;
            letter-spacing: 0.05em;
            margin-bottom: 0.5rem;
          }
        }

        .neofes-meta {
          font-size: 1.1rem;
          color: var(--color-text);
          animation: neofes-fadeInUp 1.5s ease-out 0.8s backwards;
          margin-bottom: 0;
        }

        @media (max-width: 768px) {
          .neofes-meta {
            font-size: 1rem;
          }
        }

        .neofes-scroll-indicator {
          position: absolute;
          bottom: 3rem;
          left: 50%;
          transform: translateX(-50%);
          animation: neofes-bounce 2s infinite;
        }

        @media (max-width: 768px) {
          .neofes-scroll-indicator {
            bottom: 1.5rem;
          }
        }

        .neofes-scroll-indicator span {
          display: block;
          width: 1px;
          height: 60px;
          background: linear-gradient(to bottom, var(--color-accent-1), transparent);
        }

        @keyframes neofes-bounce {
          0%, 100% { transform: translateX(-50%) translateY(0); }
          50% { transform: translateX(-50%) translateY(10px); }
        }

        @keyframes neofes-fadeInUp {
          from {
            opacity: 0;
            transform: translateY(30px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }

        /* Sections */
        .neofes-section {
          max-width: 800px;
          margin: 0 auto;
          padding: 8rem 2rem;
          position: relative;
        }

        .neofes-section-intro {
          text-align: center;
          padding: 6rem 2rem;
          background: linear-gradient(to bottom, transparent, rgba(123, 104, 238, 0.05), transparent);
        }

        .neofes-intro-text {
          font-size: 1.1rem;
          line-height: 2.4;
          color: var(--color-text);
        }

        @media (max-width: 768px) {
          .neofes-intro-text {
            font-size: 1rem;
            line-height: 2;
          }
        }

        .neofes-intro-text em {
          color: var(--color-accent-2);
          font-style: normal;
        }

        .neofes-section-title {
          font-family: var(--font-en);
          font-size: clamp(1.8rem, 5vw, 2.5rem);
          font-weight: 400;
          letter-spacing: 0.2em;
          text-align: center;
          margin-bottom: 4rem;
          position: relative;
          padding-bottom: 2rem;
        }

        @media (max-width: 768px) {
          .neofes-section-title {
            font-size: 1.6rem;
            margin-bottom: 3rem;
          }
        }

        .neofes-section-title::after {
          content: '';
          position: absolute;
          bottom: 0;
          left: 50%;
          transform: translateX(-50%);
          width: 40px;
          height: 1px;
          background: linear-gradient(90deg, transparent, var(--color-accent-1), transparent);
        }

        .neofes-content-block {
          margin-bottom: 4rem;
        }

        .neofes-content-block h3 {
          font-size: 1.2rem;
          font-weight: 500;
          color: var(--color-accent-2);
          margin-bottom: 1.5rem;
          letter-spacing: 0.1em;
        }

        @media (max-width: 768px) {
          .neofes-content-block h3 {
            font-size: 1.1rem;
          }
        }

        .neofes-content-block p {
          color: var(--color-text);
          margin-bottom: 1rem;
          font-size: 1rem;
        }

        .neofes-content-block p:last-child {
          margin-bottom: 0;
        }

        /* List with subtle styling */
        .neofes-feature-list {
          list-style: none;
          margin: 2rem 0;
        }

        .neofes-feature-list li {
          padding: 0.8rem 0;
          padding-left: 1.5rem;
          position: relative;
          color: var(--color-text);
          border-bottom: 1px solid rgba(255, 255, 255, 0.05);
          font-size: 1rem;
        }

        .neofes-feature-list li::before {
          content: '';
          position: absolute;
          left: 0;
          top: 50%;
          width: 4px;
          height: 4px;
          background: var(--color-accent-1);
          border-radius: 50%;
        }

        /* Program Section */
        .neofes-program-grid {
          display: grid;
          gap: 2rem;
        }

        .neofes-program-item {
          padding: 2rem;
          background: rgba(255, 255, 255, 0.02);
          border: 1px solid rgba(255, 255, 255, 0.05);
          border-radius: 4px;
          transition: all 0.4s ease;
        }

        .neofes-program-item:hover {
          background: rgba(255, 255, 255, 0.04);
          border-color: rgba(123, 104, 238, 0.3);
        }

        .neofes-program-item h4 {
          font-family: var(--font-en);
          font-size: 1.3rem;
          font-weight: 500;
          letter-spacing: 0.1em;
          color: var(--color-accent-2);
          margin-bottom: 1rem;
        }

        @media (max-width: 768px) {
          .neofes-program-item h4 {
            font-size: 1.2rem;
          }
        }

        .neofes-program-item p {
          font-size: 1rem;
          color: var(--color-text);
          line-height: 1.8;
        }

        /* Criteria Section */
        .neofes-criteria-box {
          background: linear-gradient(135deg, rgba(123, 104, 238, 0.1), rgba(79, 209, 197, 0.05));
          padding: 3rem;
          border-radius: 8px;
          border: 1px solid rgba(123, 104, 238, 0.2);
          margin: 3rem 0;
        }

        .neofes-criteria-box h4 {
          font-size: 1.1rem;
          font-weight: 500;
          color: var(--color-text);
          margin-bottom: 2rem;
          text-align: center;
        }

        @media (max-width: 768px) {
          .neofes-criteria-box h4 {
            font-size: 1rem;
          }
        }

        .neofes-criteria-list {
          list-style: none;
          text-align: center;
        }

        .neofes-criteria-list li {
          padding: 0.8rem 0;
          color: var(--color-text);
          font-size: 1.1rem;
        }

        @media (max-width: 768px) {
          .neofes-criteria-list li {
            font-size: 1rem;
          }
        }

        /* Details */
        .neofes-details-grid {
          display: grid;
          grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
          gap: 2rem;
          margin: 3rem 0;
        }

        .neofes-detail-item {
          text-align: center;
          padding: 1.5rem;
        }

        .neofes-detail-label {
          font-size: 1rem;
          color: var(--color-text);
          letter-spacing: 0.1em;
          margin-bottom: 0.5rem;
        }

        .neofes-detail-value {
          font-family: var(--font-en);
          font-size: 1.2rem;
          color: var(--color-text);
        }

        @media (max-width: 768px) {
          .neofes-detail-value {
            font-size: 1.1rem;
          }
        }

        /* CTA Section */
        .neofes-cta-section {
          text-align: center;
          padding: 8rem 2rem;
          background: radial-gradient(ellipse at center, rgba(123, 104, 238, 0.1) 0%, transparent 70%);
        }

        .neofes-cta-lead {
          font-size: 1.1rem;
          color: var(--color-text);
          margin-bottom: 3rem;
          line-height: 2.2;
        }

        @media (max-width: 768px) {
          .neofes-cta-lead {
            font-size: 1rem;
            line-height: 2;
          }
        }

        .neofes-cta-button {
          display: inline-block;
          padding: 1.2rem 3rem;
          font-family: var(--font-jp);
          font-size: 1rem;
          letter-spacing: 0.2em;
          color: var(--color-bg-deep);
          background: linear-gradient(135deg, var(--color-accent-1), var(--color-accent-2));
          border: none;
          border-radius: 50px;
          text-decoration: none;
          cursor: pointer;
          transition: all 0.4s ease;
          box-shadow: 0 4px 30px rgba(123, 104, 238, 0.3);
        }

        .neofes-cta-button:hover {
          transform: translateY(-2px);
          box-shadow: 0 8px 40px rgba(123, 104, 238, 0.5);
        }

        /* Closing */
        .neofes-closing {
          text-align: center;
          padding: 6rem 2rem 8rem;
        }

        .neofes-closing p {
          color: var(--color-text);
          line-height: 2.4;
          max-width: 600px;
          margin: 0 auto;
          font-size: 1rem;
        }

        @media (max-width: 768px) {
          .neofes-closing p {
            line-height: 2;
          }
        }

        .neofes-closing .neofes-highlight {
          color: var(--color-accent-2);
          display: block;
          margin-top: 2rem;
        }

        /* Footer */
        .neofes-footer {
          text-align: center;
          padding: 3rem 2rem;
          border-top: 1px solid rgba(255, 255, 255, 0.05);
        }

        .neofes-footer p {
          font-family: var(--font-en);
          font-size: 0.95rem;
          color: var(--color-text);
          letter-spacing: 0.2em;
        }

        /* Divider */
        .neofes-divider {
          width: 100%;
          height: 1px;
          background: linear-gradient(90deg, transparent, rgba(123, 104, 238, 0.3), transparent);
          margin: 4rem 0;
        }

        /* Responsive */
        @media (max-width: 768px) {
          .neofes-section {
            padding: 2rem 1.5rem;
          }

          .neofes-section-intro {
            padding: 2rem 1.5rem;
          }

          .neofes-cta-section {
            padding: 2rem 1.5rem;
          }

          .neofes-closing {
            padding: 2rem 1.5rem 2rem;
          }

          .neofes-footer {
            padding: 2rem 1.5rem;
          }

          .neofes-header {
            padding: 0;
            min-height: 100vh;
          }

          .neofes-criteria-box {
            padding: 2rem 1.5rem;
          }
        }

        /* Fade in animation on scroll */
        .fade-in {
          opacity: 0;
          transform: translateY(20px);
          transition: opacity 0.8s ease, transform 0.8s ease;
        }

        .fade-in.visible {
          opacity: 1;
          transform: translateY(0);
        }
      `}</style>

      <div className="neofes-root">
        <div className="neofes-cosmic-bg"></div>
        <div className="neofes-stars" id="stars"></div>

        <header className="neofes-header">
          <div className="neofes-header-content">
            <h1 className="neofes-logo">
              <span className="hidden md:inline">Neo Earth Culture Fes</span>
              <span className="md:hidden">Neo Earth<br />Culture Fes</span>
            </h1>
            <p className="neofes-year">2026</p>
            <div className="neofes-tagline">
              <span>自分の創造性とつながり</span>
              <span>周波数で共鳴して</span>
              <span>新しい地球を生み出す</span>
            </div>
            <p className="neofes-dates">2026.3.5（木）～2026.3.7（土）</p>
            <p className="neofes-meta">オンライン開催・参加無料</p>
          </div>
          <div className="neofes-scroll-indicator">
            <span></span>
          </div>
        </header>

        <div className="neofes-section-intro">
          <p className="neofes-intro-text fade-in">
            このページは、登壇をお願いしたい方のためのご案内です。<br /><br />
            Neo Earth Culture Fes は<br />
            講師を集めるイベントではありません。<br />
            有名な人を並べるフェスでもありません。<br /><br />
            <em>この場で一番大切にしているのは<br />
            その人が、どう生きているか<br />
            ただそれだけです。</em>
          </p>
        </div>

        <section className="neofes-section">
          <h2 className="neofes-section-title fade-in">What is this?</h2>
          
          <div className="neofes-content-block fade-in">
            <p>
              これは<br />
              学ぶためのフェスではありません。<br />
              答えをもらうフェスでもありません。
            </p>
          </div>

          <div className="neofes-content-block fade-in">
            <p>
              感じるための場<br />
              共鳴するための場<br />
              思い出すための場
            </p>
          </div>

          <div className="neofes-content-block fade-in">
            <p>
              自分の内側と<br />
              他者と<br />
              世界と<br /><br />
              静かに、深く<br />
              つながり直す3日間です。
            </p>
          </div>
        </section>

        <section className="neofes-section">
          <h2 className="neofes-section-title fade-in">Why now?</h2>
          
          <div className="neofes-content-block fade-in">
            <p>
              多くの人が<br />
              引き寄せやアファメーションを知っています。<br /><br />
              でも、変わらない。<br /><br />
              なぜか。<br /><br />
              それは<br />
              内側の本当の状態を知らないまま<br />
              言葉だけを重ねているから。
            </p>
          </div>

          <div className="neofes-content-block fade-in">
            <p>
              このフェスでは<br />
              理屈を超えて<br />
              体感から始めます。<br /><br />
              一人では難しいことも<br />
              場の周波数の中では起こります。<br /><br />
              集まり<br />
              共鳴し<br />
              同じ空気に身を置く。<br /><br />
              <em style={{ color: 'var(--color-accent-2)' }}>それ自体が、変化です。</em>
            </p>
          </div>
        </section>

        <section className="neofes-section">
          <h2 className="neofes-section-title fade-in">What happens?</h2>
          
          <ul className="neofes-feature-list fade-in">
            <li>ジャッジのない安心空間</li>
            <li>上下のない、魂の対等性</li>
            <li>教える人と教わる人に分かれない</li>
            <li>多様な世代と価値観が混ざり合う</li>
            <li>聴くだけで終わらない</li>
          </ul>

          <p className="fade-in" style={{ textAlign: 'center', color: 'var(--color-text)', marginTop: '3rem' }}>
            共同創造の場が生まれます。
          </p>
        </section>

        <section className="neofes-section">
          <h2 className="neofes-section-title fade-in">Program</h2>
          <p className="fade-in" style={{ textAlign: 'center', color: 'var(--color-text)', marginBottom: '3rem', fontSize: '1rem' }}>
            ※ 一方的な講演は行いません
          </p>

          <div className="neofes-program-grid">
            <div className="neofes-program-item fade-in">
              <h4>Opening</h4>
              <p>言葉を超えて<br />場の周波数を整える時間</p>
            </div>

            <div className="neofes-program-item fade-in">
              <h4>世代クロストーク</h4>
              <p>10代・20代・30代・40代・50代以上<br />子ども・若者・大人が混ざり合う対話<br /><br />テーマ「どうやってピュアセルフに還ってきたか」</p>
            </div>

            <div className="neofes-program-item fade-in">
              <h4>共鳴セッション</h4>
              <p>瞑想・内観・体感ワーク<br />理解ではなく、感覚で</p>
            </div>

            <div className="neofes-program-item fade-in">
              <h4>共同創造タイム</h4>
              <p>参加者も声を出す<br />言葉や想いを場に置く</p>
            </div>

            <div className="neofes-program-item fade-in">
              <h4>Closing</h4>
              <p>この3日間の振動を<br />どう未来につなぐか</p>
            </div>
          </div>
        </section>

        <section className="neofes-section">
          <h2 className="neofes-section-title fade-in">登壇について</h2>
          
          <div className="neofes-content-block fade-in">
            <p style={{ textAlign: 'center' }}>
              登壇といっても<br />
              講義はありません。<br />
              売り込みはありません。<br />
              台本もありません。<br /><br />
              基本は<br />
              対談・クロストーク・対話形式です。<br /><br />
              その瞬間に感じたことを<br />
              等身大で語れる</p>
          </div>

          <div className="neofes-criteria-box fade-in">
            <h4>このフェスが大切にする基準</h4>
            <p style={{ textAlign: 'center', color: 'var(--color-text)', marginBottom: '2rem', fontSize: '1rem' }}>
              有名かどうか、フォロワー数、実績<br />
              これらは一切、基準にしていません。
            </p>
            <ul className="neofes-criteria-list">
              <li>生き方が振り切れている</li>
              <li>ビジョンを持ち、動き始めている</li>
              <li>自然と巻き込みが起きている</li>
              <li>等身大で語れる</li>
            </ul>
            <p style={{ textAlign: 'center', color: 'var(--color-text)', marginTop: '2rem', fontSize: '1rem' }}>
              そんな人たちが<br />
              世代もジャンルも越えて集います。
            </p>
          </div>
        </section>

        <section className="neofes-section">
          <h2 className="neofes-section-title fade-in">Details</h2>
          
          <div className="neofes-details-grid fade-in" style={{ display: 'flex', flexDirection: 'column', gap: '1.5rem', maxWidth: '600px', margin: '0 auto' }}>
            <div className="neofes-detail-item" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '1.5rem', borderBottom: '1px solid rgba(255, 255, 255, 0.1)' }}>
              <p className="neofes-detail-label" style={{ margin: 0 }}>開催日</p>
              <p className="neofes-detail-value" style={{ margin: 0 }}>2026.3.5-7</p>
            </div>
            <div className="neofes-detail-item" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '1.5rem', borderBottom: '1px solid rgba(255, 255, 255, 0.1)' }}>
              <p className="neofes-detail-label" style={{ margin: 0 }}>時間</p>
              <p className="neofes-detail-value" style={{ margin: 0 }}>21:00 — 23:00</p>
            </div>
            <div className="neofes-detail-item" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '1.5rem', borderBottom: '1px solid rgba(255, 255, 255, 0.1)' }}>
              <p className="neofes-detail-label" style={{ margin: 0 }}>形式</p>
              <p className="neofes-detail-value" style={{ margin: 0 }}>Online</p>
            </div>
            <div className="neofes-detail-item" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '1.5rem' }}>
              <p className="neofes-detail-label" style={{ margin: 0 }}>参加費</p>
              <p className="neofes-detail-value" style={{ margin: 0 }}>無料</p>
            </div>
          </div>
        </section>

        <section className="neofes-section">
          <h2 className="neofes-section-title fade-in">Goal</h2>
          
          <div className="neofes-content-block fade-in">
            <p style={{ textAlign: 'center' }}>
              このフェスのゴールは<br />
              何かを達成することではありません。<br /><br />
              資格を取ることでも<br />
              変化を証明することでもありません。<br /><br />
              <span style={{ color: 'var(--color-accent-2)' }}>参加後<br />
              自分のピュアセルフを<br />
              信頼できている状態に戻ること</span><br /><br />
              それだけです。
            </p>
          </div>

          <div className="neofes-divider"></div>

          <div className="neofes-content-block fade-in">
            <h3 style={{ textAlign: 'center' }}>フェスのその先について</h3>
            <p style={{ textAlign: 'center' }}>
              フェスの最後に<br />
              ひとつだけ、お伝えします。<br /><br />
              「この場を<br />
              一緒に育てていく人のための<br />
              コミュニティがあります」<br /><br />
              申し込みを煽ることはしません。<br />
              今すぐ決める必要もありません。<br /><br />
              余韻の中で<br />
              自分の感覚で<br />
              選んでいただけたらと思っています。
            </p>
          </div>
        </section>

        <div className="neofes-cta-section">
          <p className="neofes-cta-lead fade-in">
            少しでも<br />
            「面白そう」<br />
            「震える」<br />
            「関わってみたい」<br /><br />
            そう感じたら<br />
            まずは直感で大丈夫です。
          </p>
          <a href="https://forms.gle/gGSkrL2YQcrsqEzL8" target="_blank" rel="noopener noreferrer" className="neofes-cta-button fade-in">
            登壇者エントリー
          </a>
        </div>

        <div className="neofes-closing">
          <p className="fade-in">
            このフェスは<br />
            完成された企画ではありません。<br /><br />
            集まる人の周波数によって<br />
            形が変わります。<br /><br />
            だからこそ<br />
            一緒につくってほしいと思っています。<br /><br />
            <span className="neofes-highlight">
              あなたの生き方の温度を<br />
              この場に置いてもらえたら<br />
              嬉しいです。
            </span>
          </p>
        </div>

        <footer className="neofes-footer">
          <p>Neo Earth Culture Fes 2026</p>
        </footer>
      </div>
    </>
  );
}
